import { useState, useRef, useEffect, useLayoutEffect, useCallback, RefObject, CSSProperties } from 'react';

interface UseDropdownPositionOptions {
    /**
     * Initial guess for height, used for first-render positioning.
     * @default 160
     */
    defaultHeight?: number;
    /**
     * Initial guess for width, used for first-render right alignment.
     * @default 176
     */
    defaultWidth?: number;
    /**
     * CSS selector for a "boundary" element.
     */
    boundarySelector?: string;
    onScroll?: 'close' | 'reposition';
}

interface UseDropdownPositionReturn {
    isOpen: boolean;
    style: CSSProperties;
    buttonRef: RefObject<HTMLButtonElement>;
    dropdownRef: RefObject<HTMLDivElement>;
    toggle: () => void;
    close: () => void;
}

/**
 * useDropdownPosition
 *
 * Fully dynamic dropdown positioning hook.
 *
 * Features:
 * 1. Measures actual dropdown dimensions (via useLayoutEffect)
 * 2. Flips upward if space is tight (using 'bottom' positioning)
 * 3. Aligns right edge to button (using actual width)
 * 4. Respects boundary elements (pagination bar)
 */
export function useDropdownPosition({
    defaultHeight = 160,
    defaultWidth = 176,
    boundarySelector,
    onScroll = 'close',
}: UseDropdownPositionOptions = {}): UseDropdownPositionReturn {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [style, setStyle] = useState<CSSProperties>({
        opacity: 0,
        pointerEvents: 'none',
        position: 'fixed'
    });

    const computePosition = useCallback(() => {
        if (!buttonRef.current || !dropdownRef.current) return null;

        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownRect = dropdownRef.current.getBoundingClientRect();

        // Use actual dimensions
        const height = dropdownRect.height || defaultHeight;
        const width = dropdownRect.width || defaultWidth;

        // Determine bottom boundary
        let bottomBoundary = window.innerHeight;
        if (boundarySelector) {
            const boundaryEl = document.querySelector(boundarySelector);
            if (boundaryEl) {
                bottomBoundary = boundaryEl.getBoundingClientRect().top;
            }
        }

        const spaceBelow = bottomBoundary - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        let newStyle: CSSProperties = {
            position: 'fixed',
            opacity: 1, // Make visible
            pointerEvents: 'auto',
            zIndex: 9999
        };

        // Horizontal: Right-align to button
        // left = button.right - dropdown.width
        newStyle.left = Math.max(0, buttonRect.right - width);

        // Vertical logic
        let shouldFlip = false;
        if (spaceBelow >= height) {
            shouldFlip = false;
        } else if (spaceAbove >= height) {
            shouldFlip = true;
        } else {
            // Pick side with more space
            shouldFlip = spaceAbove > spaceBelow;
        }

        if (shouldFlip) {
            newStyle.bottom = window.innerHeight - buttonRect.top + 4;
            newStyle.top = 'auto';
        } else {
            newStyle.top = buttonRect.bottom + 4;
            newStyle.bottom = 'auto';
        }

        return newStyle;
    }, [defaultHeight, defaultWidth, boundarySelector]);

    const toggle = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    const close = useCallback(() => setIsOpen(false), []);

    // Re-compute when open (using useLayoutEffect to avoid flicker)
    useLayoutEffect(() => {
        if (!isOpen) {
            // Reset style when closed so next open starts invisible
            setStyle({ opacity: 0, pointerEvents: 'none', position: 'fixed' });
            return;
        }

        const pos = computePosition();
        if (pos) setStyle(pos);

    }, [isOpen, computePosition]);

    // Handle scroll/resize
    useEffect(() => {
        if (!isOpen) return;

        const handleUpdate = () => {
            if (onScroll === 'close') {
                close();
            } else {
                const pos = computePosition();
                if (pos) setStyle(pos);
            }
        };

        window.addEventListener('scroll', handleUpdate, { passive: true, capture: true });
        window.addEventListener('resize', handleUpdate, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleUpdate, { capture: true });
            window.removeEventListener('resize', handleUpdate);
        };
    }, [isOpen, onScroll, computePosition, close]);

    return { isOpen, style, buttonRef, dropdownRef, toggle, close };
}
